import React, { useEffect, useRef, useState } from 'react';
import { Delete, ScanLine } from 'lucide-react';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
const QUICK = ['S', 'V', 'G', 'E', 'N'];
const ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/** Big arm's-length keypad for typing a Staff ID on the section device; a USB / Bluetooth ID-card scanner also works. */
export const KeypadEntry = ({ value, onChange, onSubmit, disabled }) => {
  const [abc, setAbc] = useState(false);
  const cur = useRef(value);
  const submitRef = useRef(onSubmit);
  const changeRef = useRef(onChange);
  cur.current = value;
  submitRef.current = onSubmit;
  changeRef.current = onChange;
  const press = (k) => !disabled && onChange((value + k).slice(0, 24));
  useEffect(() => {
    if (disabled) return undefined;
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || ['INPUT', 'TEXTAREA'].includes(e.target?.tagName)) return;
      if (e.key === 'Enter') { e.preventDefault(); if (cur.current.trim()) submitRef.current(); return; }
      if (e.key === 'Backspace') { cur.current = cur.current.slice(0, -1); changeRef.current(cur.current); return; }
      if (e.key.length === 1 && /[A-Za-z0-9 :\-/]/.test(e.key)) { cur.current = (cur.current + e.key.toUpperCase()).slice(0, 24); changeRef.current(cur.current); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled]);
  const Key = ({ k, label, cls = '', onClick }) => (
    <button type="button" onClick={onClick || (() => press(k))} disabled={disabled}
      className={`h-20 rounded-2xl text-3xl font-bold bg-white/95 text-slate-900 shadow-lg active:scale-95 transition-transform disabled:opacity-40 ${cls}`}
      data-testid={`kiosk-key-${k}`}>{label || k}</button>
  );
  const Letter = ({ l, cls = 'h-14 w-14 text-2xl' }) => (
    <button type="button" onClick={() => press(l)} disabled={disabled}
      className={`${cls} rounded-xl font-bold bg-amber-400 text-slate-900 shadow active:scale-95 transition-transform`}
      data-testid={`kiosk-key-${l.toLowerCase()}`}>{l}</button>
  );
  return (
    <div className="w-full max-w-md mx-auto space-y-4">
      <div className="h-20 rounded-2xl bg-slate-800 border-2 border-slate-600 flex items-center justify-center text-4xl font-mono font-bold tracking-widest text-white" data-testid="kiosk-id-display">
        {value || <span className="text-slate-500 text-2xl font-sans tracking-normal">Type your Staff ID</span>}
      </div>
      <div className="flex items-center justify-center gap-1 text-sm text-slate-400" data-testid="kiosk-scan-hint"><ScanLine className="w-4 h-4" />or scan your ID card</div>
      {abc ? (
        <div className="grid grid-cols-7 gap-2" data-testid="kiosk-alpha-panel">
          {ALPHA.map((l) => <Letter key={l} l={l} cls="h-12 text-xl" />)}
          <button type="button" onClick={() => setAbc(false)} className="h-12 rounded-xl text-sm font-bold bg-slate-600 text-white" data-testid="kiosk-key-abc-close">123</button>
        </div>
      ) : (
        <div className="flex gap-2 justify-center">
          {QUICK.map((l) => <Letter key={l} l={l} />)}
          <button type="button" onClick={() => setAbc(true)} className="h-14 px-3 rounded-xl text-lg font-bold bg-slate-600 text-white shadow active:scale-95 transition-transform" data-testid="kiosk-key-abc">ABC</button>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        {KEYS.map((k) => <Key key={k} k={k} />)}
        <Key k="del" label={<Delete className="w-8 h-8 mx-auto" />} onClick={() => onChange(value.slice(0, -1))} cls="bg-slate-300" />
        <Key k="0" />
        <button type="button" onClick={onSubmit} disabled={disabled || !value}
          className="h-20 rounded-2xl text-2xl font-bold bg-emerald-500 text-white shadow-lg active:scale-95 transition-transform disabled:opacity-40"
          data-testid="kiosk-key-ok">OK</button>
      </div>
    </div>
  );
};

export default KeypadEntry;
